
import React from 'react';

const AboutUs = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section 
        className="py-24 border-b border-border relative bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(255,255,255,0.85), rgba(255,255,255,0.9)), url('https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1920&q=80')`
        }}
      >
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6">
            About Law and Code Africa
          </h1>
          <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
            Bridging the gap between technology and legal frameworks through expert consultation 
            in AI law, cyber law, and technology policy across Africa and beyond.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <h2 className="text-3xl font-bold text-primary mb-8">Our Mission</h2>
          <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
            <p>
              At lawandcode.africa, we are dedicated to navigating the complex landscape where 
              technology meets legal frameworks. Our mission is to provide expert guidance, 
              comprehensive research, and strategic consultation in AI law, cyber law, and 
              technology policy.
            </p>
            <p>
              We specialize in helping organizations understand and comply with emerging 
              regulations while fostering innovation and ethical technology development. 
              Our expertise spans the intersection of artificial intelligence, cybersecurity, 
              data protection, and commercial technology law.
            </p>
          </div>
        </div>
      </section>

      {/* Areas of Expertise */}
      <section id="areas-of-expertise" className="py-20 border-t border-border">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <h2 className="text-3xl font-bold text-primary mb-12">Areas of Expertise</h2>
          
          <div className="space-y-16">
            {/* AI Law */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-primary mb-2">Artificial Intelligence Law</h3>
              </div>
              <div className="md:col-span-2">
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Comprehensive legal guidance on AI governance, algorithmic accountability, 
                  and regulatory compliance. We help organizations navigate the evolving 
                  landscape of AI regulations including the EU AI Act, data protection 
                  requirements, and ethical AI frameworks.
                </p>
                <ul className="text-muted-foreground space-y-2">
                  <li>— AI compliance audits and risk assessments</li>
                  <li>— Algorithmic accountability frameworks</li>
                  <li>— Machine learning and data usage policies</li>
                  <li>— AI ethics and governance consulting</li>
                </ul>
              </div>
            </div>

            {/* Cyber Law */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-primary mb-2">Cyber Law & Data Protection</h3>
              </div>
              <div className="md:col-span-2">
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Expert guidance on cybersecurity regulations, data protection compliance, 
                  and digital privacy frameworks. We assist clients in understanding their 
                  obligations under GDPR, Kenya's Data Protection Act, and other privacy 
                  legislation.
                </p>
                <ul className="text-muted-foreground leading-relaxed space-y-2">
                  <li>— Data protection impact assessments</li>
                  <li>— Cybersecurity policy development</li>
                  <li>— Privacy compliance strategies</li>
                  <li>— Incident response and breach notification</li>
                </ul>
              </div>
            </div>

            {/* Technology Law */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-primary mb-2">Technology & Commercial Law</h3>
              </div>
              <div className="md:col-span-2">
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Strategic legal support for technology companies and startups navigating 
                  intellectual property, software licensing, and commercial agreements. 
                  We understand the unique challenges facing tech enterprises in today's 
                  digital economy.
                </p>
                <ul className="text-muted-foreground space-y-2">
                  <li>— Technology licensing and SaaS agreements</li>
                  <li>— Intellectual property protection</li>
                  <li>— Startup legal structuring</li>
                  <li>— Tech vendor and procurement contracts</li>
                </ul>
              </div>
            </div>

            {/* Dispute Resolution */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-primary mb-2">Arbitration & Mediation</h3>
              </div>
              <div className="md:col-span-2">
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Professional alternative dispute resolution services for technology, 
                  commercial, and intellectual property conflicts. We provide efficient 
                  and confidential resolution through arbitration and mediation.
                </p>
                <ul className="text-muted-foreground space-y-2">
                  <li>— Technology and software disputes</li>
                  <li>— Commercial contract conflicts</li>
                  <li>— IP and trademark disagreements</li>
                  <li>— Cross-border dispute resolution</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 border-t border-border">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <h2 className="text-3xl font-bold text-primary mb-8">Our Approach</h2>
          <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
            <p>
              We combine deep legal expertise with practical understanding of technology 
              to deliver solutions that are both legally sound and business-oriented. 
              Our approach is collaborative, working closely with clients to understand 
              their unique challenges and objectives.
            </p>
            <p>
              Whether you're a startup navigating regulatory compliance, an established 
              company implementing AI systems, or an organization seeking to strengthen 
              your cybersecurity posture, we provide tailored guidance that addresses 
              your specific needs.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
